import { EnfantsStructure, ObjetMaquetteStructure } from '@pcscol/odf-api-v1-angular';
import { TreeViewFiltres } from '../../services/tree-view.service';

/**
 * Nécessaire pour gérer les détachements (lors du détachement, on transmet le noeud à détacher).
 *
 * On doit déterminer à partir de ce noeud avec quel parent le détacher.
 */
export interface TreeNode extends ObjetMaquetteStructure {
    // permet d'identifier de manière unique le chemin (il s'agit de la concaténation des id des oms)
    path: string;
    enfants: Array<TreeEnfant>;
    racine: boolean;
    level: number;
    obligatoire: boolean;
    piaPresentSurUnAncetre: { piaActif: boolean; brancheValide: boolean };
    estEnfantDeGroupement?: boolean;
    containsText?: boolean;
    expandable?: boolean;
}

export interface HistoryTreeNodeComparisonItem {
    obligatoire: boolean;
    id: string;
    libelle: string;
}

export interface HistoryTreeNode {
    code: string;
    codesPath: string;
    racine: boolean;
    level?: number;
    left?: HistoryTreeNodeComparisonItem;
    right?: HistoryTreeNodeComparisonItem;
    enfants: HistoryTreeNode[];
}

export interface TreeEnfant extends EnfantsStructure {
    objetMaquette: TreeNode;
}

export function compareTreeEnfants(a: EnfantsStructure, b: EnfantsStructure): number {
    const ordreA = a.ordre;
    const ordreB = b.ordre;

    if (ordreA != null && ordreB != null) {
        const ordreCompare = ordreA - ordreB;
        if (ordreCompare !== 0) {
            return ordreCompare;
        }
    }

    const identical = a.objetMaquette.classe === b.objetMaquette.classe;
    if (identical) {
        return a.objetMaquette.code.localeCompare(b.objetMaquette.code);
    }

    return a.objetMaquette.classe === 'G' ? 1 : -1;
}

export function historyTreeNode(
    code: string,
    left: ObjetMaquetteStructure,
    right: ObjetMaquetteStructure,
    leftObligatoire: boolean,
    rightObligatoire: boolean,
    parentCodesPath?: string
): HistoryTreeNode {
    const codesPath = parentCodesPath ? `${parentCodesPath}.${code}` : code;
    const racine = !parentCodesPath;

    return {
        code,
        codesPath,
        racine,
        // level
        left: left ? { obligatoire: leftObligatoire, id: left.id, libelle: left.libelle! } : undefined,
        right: right ? { obligatoire: rightObligatoire, id: right.id, libelle: right.libelle! } : undefined,
        enfants: historyTreeNodeChildrenRecursion(left.enfants, right.enfants, codesPath)
    };
}

function historyTreeNodeChildrenRecursion(leftEnfants, rightEnfants, parentCodesPath: string): HistoryTreeNode[] {
    const enfantsLeft: HistoryTreeNode[] =
        leftEnfants?.map((e: EnfantsStructure) => {
            const node: Partial<HistoryTreeNode> & { tempEnfantsLeft: EnfantsStructure[]; tempEnfantsRight: EnfantsStructure[] } = {
                code: e.objetMaquette.code,
                codesPath: `${parentCodesPath}.${e.objetMaquette.code}`,
                racine: false,
                left: {
                    obligatoire: e.obligatoire,
                    id: e.objetMaquette.id,
                    libelle: e.objetMaquette.libelle!
                },
                tempEnfantsLeft: e.objetMaquette.enfants ?? [],
                tempEnfantsRight: []
            };
            return node;
        }) ?? [];
    const enfants = rightEnfants.reduce((acc, val) => {
        if (acc.some(node => node.code === val.objetMaquette.code)) {
            const modified = {
                ...acc.find(node => node.code === val.objetMaquette.code),
                right: {
                    obligatoire: val.obligatoire,
                    id: val.objetMaquette.id,
                    libelle: val.objetMaquette.libelle
                },
                tempEnfantsRight: val.objetMaquette.enfants ?? []
            };
            acc = [...acc.filter(node => node.code !== val.objetMaquette.code), modified];
        } else {
            acc = [
                ...acc,
                {
                    code: val.objetMaquette.code,
                    codesPath: `${parentCodesPath}.${val.objetMaquette.code}`,
                    racine: false,
                    right: {
                        obligatoire: val.obligatoire,
                        id: val.objetMaquette.id,
                        libelle: val.objetMaquette.libelle
                    },
                    tempEnfantsRight: val.objetMaquette.enfants ?? []
                }
            ];
        }
        return acc;
    }, enfantsLeft);
    return enfants.map(element => {
        return {
            ...element,
            enfants: historyTreeNodeChildrenRecursion(element.tempEnfantsLeft, element.tempEnfantsRight, element.codesPath)
        };
    });
}

function treeNode(objetMaquette: TreeNode, obligatoire: boolean, parentPath?: string): TreeNode {
    const path = parentPath ? parentPath + '.' + objetMaquette.id : objetMaquette.id;
    const racine = parentPath ? false : true;
    return {
        ...objetMaquette,
        path: path,
        racine: racine,
        obligatoire: obligatoire,
        enfants: objetMaquette?.enfants
            .map(e => {
                return {
                    ...e,
                    objetMaquette: treeNode(
                        {
                            ...e.objetMaquette,
                            piaPresentSurUnAncetre: objetMaquette.pia
                                ? {
                                      piaActif: !!objetMaquette.pia.actif || objetMaquette.piaPresentSurUnAncetre?.piaActif,
                                      brancheValide: objetMaquette.valide
                                  }
                                : objetMaquette.piaPresentSurUnAncetre,
                            estEnfantDeGroupement: objetMaquette.classe === 'G'
                        },
                        e.obligatoire,
                        path
                    )
                };
            })
            .sort(sortNodesFunction)
    } as TreeNode;
}

function treeNodeWithFilter(
    objetMaquette: ObjetMaquetteStructure & TreeNode,
    obligatoire: boolean,
    filter: TreeViewFiltres,
    parentPath?: string
): TreeNode {
    const path = parentPath ? parentPath + '.' + objetMaquette.id : objetMaquette.id;
    const racine = parentPath ? false : true;
    const shouldHideChildren: boolean = !filter.partieBasse && !!(objetMaquette.pia || objetMaquette.piaPresentSurUnAncetre); // n'est pas coché partie basse
    return {
        ...objetMaquette,
        path: path,
        racine: racine,
        obligatoire,
        enfants: shouldHideChildren
            ? []
            : objetMaquette?.enfants
                  .map(e => {
                      return {
                          ...e,
                          objetMaquette: treeNodeWithFilter(
                              {
                                  ...e.objetMaquette,
                                  piaPresentSurUnAncetre: objetMaquette.pia
                                      ? { piaActif: true, brancheValide: objetMaquette.valide }
                                      : objetMaquette.piaPresentSurUnAncetre,
                                  estEnfantDeGroupement: objetMaquette.classe === 'G'
                              } as TreeNode,
                              e.obligatoire,
                              filter,
                              path
                          )
                      };
                  })
                  .sort(sortNodesFunction)
    } as TreeNode;
}

export const BRANCHE_VIDE = 'Branche vide';

const searchPias = (objetMaquette: ObjetMaquetteStructure & TreeNode, parentPath?: string) => {
    const path = parentPath ? parentPath + '.' + objetMaquette.id : objetMaquette.id;
    const racine = parentPath ? false : true;
    if (objetMaquette.pia) {
        return [{ ...objetMaquette, path, racine }];
    }
    if (objetMaquette.enfants.length) {
        return objetMaquette.enfants.map(e => searchPias(e.objetMaquette as TreeNode, path)).flat();
    }
    return BRANCHE_VIDE;
};

export function treeNodeWithFiltered(
    objetMaquette: ObjetMaquetteStructure & TreeNode,
    filter: TreeViewFiltres,
    parentPath?: string
): { nodes: TreeNode[]; branchesVidesPartieBasse?: boolean } {
    if (filter.partieBasse && filter.partieHaute) {
        return { nodes: [treeNode(objetMaquette, true, parentPath)] };
    }
    if (filter.partieHaute) {
        return { nodes: [treeNodeWithFilter(objetMaquette, true, filter, parentPath)] };
    }
    const piasTrouves = searchPias(objetMaquette);
    if (piasTrouves === BRANCHE_VIDE) {
        return {
            nodes: [],
            branchesVidesPartieBasse: true
        };
    }
    return {
        nodes: piasTrouves.filter(e => e !== BRANCHE_VIDE).map(e => treeNode(e, e.path)),
        branchesVidesPartieBasse: piasTrouves.some(e => e !== BRANCHE_VIDE)
    };
}

const sortNodesFunction = (a, b) => {
    return compareTreeEnfants(a, b);
};
